import React, { useEffect, useRef, useState } from 'react';
import { Upload, Trash2, Play, Image as ImageIcon, Film } from 'lucide-react';
import { api, mediaUrl, chunkedUpload } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const CATEGORIES = ['Garden', 'Gardens', 'Construction', 'Events', 'Wildlife', 'People'];

const MediaManager = () => {
  const { toast } = useToast();
  const [media, setMedia] = useState([]);
  const [category, setCategory] = useState('Garden');
  const [title, setTitle] = useState('');
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get('/media').then((r) => setMedia(r.data || [])).catch(() => setMedia([]));
  useEffect(() => { load(); }, []);

  const onFiles = async (files) => {
    const list = Array.from(files);
    for (const file of list) {
      setProgress(0);
      try {
        // eslint-disable-next-line no-await-in-loop
        await chunkedUpload(file, { category, title: title || file.name }, setProgress);
        toast({ title: 'Uploaded', description: file.name });
      } catch (err) {
        toast({ title: 'Upload failed', description: file.name, variant: 'destructive' });
      }
    }
    setProgress(null);
    setTitle('');
    if (fileRef.current) fileRef.current.value = '';
    load();
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await api.delete(`/media/${id}`);
      setMedia((m) => m.filter((x) => x.id !== id));
      toast({ title: 'Deleted' });
    } catch {
      toast({ title: 'Could not delete', variant: 'destructive' });
    }
  };

  return (
    <div>
      <div className="bg-white rounded-2xl border border-sand p-6 mb-8">
        <h3 className="font-display text-xl text-forest mb-4">Upload photos & videos</h3>
        <div className="grid sm:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-xs font-600 text-ink/60 mb-1">Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-sand text-sm focus:border-gold focus:outline-none">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-600 text-ink/60 mb-1">Title (optional)</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-sand text-sm focus:border-gold focus:outline-none" placeholder="Describe this media" />
          </div>
        </div>
        <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={(e) => e.target.files.length && onFiles(e.target.files)} />
        <button onClick={() => fileRef.current?.click()} disabled={progress !== null} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold text-white font-600 text-sm hover:bg-gold-light transition-colors disabled:opacity-60">
          <Upload className="w-4 h-4" /> {progress !== null ? `Uploading ${progress}%` : 'Choose files'}
        </button>
        {progress !== null && (
          <div className="mt-4 h-2 rounded-full bg-sand overflow-hidden"><div className="h-full bg-gold transition-all" style={{ width: `${progress}%` }} /></div>
        )}
        <p className="text-xs text-ink/45 mt-3">Large videos are uploaded in chunks. Supported: images & video files.</p>
      </div>

      <h3 className="font-display text-xl text-forest mb-4">Library ({media.length})</h3>
      {media.length === 0 ? (
        <p className="text-ink/50 text-sm">No media uploaded yet.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {media.map((m) => (
            <div key={m.id} className="group relative rounded-xl overflow-hidden border border-sand bg-white">
              <div className="relative h-40 bg-sand">
                {m.type === 'video' ? (
                  <div className="w-full h-full flex items-center justify-center bg-forest-dark">
                    <video src={mediaUrl(m.url)} className="w-full h-full object-cover opacity-80" muted preload="metadata" />
                    <Play className="w-8 h-8 text-white absolute" />
                  </div>
                ) : (
                  <img src={mediaUrl(m.url)} alt={m.title} className="w-full h-full object-cover" />
                )}
                <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-forest/90 text-cream text-[10px] font-600 flex items-center gap-1">{m.type === 'video' ? <Film className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}{m.category}</span>
              </div>
              <div className="p-3 flex items-center justify-between gap-2">
                <span className="text-xs text-ink/70 truncate">{m.title}</span>
                <button onClick={() => remove(m.id)} className="shrink-0 w-8 h-8 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaManager;
